import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { FormGroupDirective, UntypedFormGroup, Validators } from '@angular/forms';
import {
	CandidateAccount,
	CertRequirementCatalogDetail,
	CertRequirementDetail,
	CrcatType,
	Culture,
	HubFeature,
	HubPermission,
	PortalType,
} from '@ats/api/cm';
import { CmAuthService, Guard } from '@ats/cm/core';
import { CmNumericValidatorVariation, CmValidators } from '@ats/cm/ui';
import { AtsNotificationService } from '@ats/notifications';
import { AtsFileUploadService } from '@ats/utility';
import { LOC_INJECTOR, LocalizationProvider } from '@cm/core/localization';
import { SettingsService } from '@cm/core/settings';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Apollo, gql } from 'apollo-angular';
import { DocumentNode } from 'graphql';
import { combineLatest, Observable, of } from 'rxjs';
import {
	distinctUntilChanged,
	distinctUntilKeyChanged,
	filter,
	map,
	startWith,
	switchMap,
	take,
	withLatestFrom,
} from 'rxjs/operators';
import { CMOption } from '../../../models';
import { CmCreqUploadService } from '../creq-upload.service';
import { DEFAULT_DOCUMENTATION_VALIDATORS } from '../default-documentation-validators';
import {
	CPUploadConfirmationLocKeys,
	cpUploadConfirmationLocs,
	SPUploadConfirmationLocKeys,
	spUploadConfirmationLocs,
} from '../upload-confirmation/upload-confirmation.locs';
import { CAND_CRCAT_QUERY, CE_PROGRESS_QUERY } from '../upload-refresh-queries';
import {
	CPUploadFormLocKeys,
	cpUploadFormLocs,
	SPUploadFormLocKeys,
	spUploadFormLocs,
} from './upload-form.locs';

const CREQ_CULTURES = gql`
	query creqCultures($request: CulturesRequest) {
		cultures(request: $request) {
			id
			name
			label
		}
	}
`;

const CRCAT_QUERY = (guard: Guard) => gql`
	query certRequirementCatalogDetail($request: RequestByIdOrExternalId!) {
		certRequirementCatalogDetail(request: $request) {
			instructions
			providerRequired
			startDate
			createAge
			docRequired
			id
			type
			${
				guard
					.reset()
					.everyFeature([HubFeature.CredentialContinuingEducation])
					.somePermissions([
						HubPermission.CandidateRequirementsContinuingEducationView,
						HubPermission.RequirementsContinuingEducationCatalogView,
					]).all
					? `
					factor
					factorUnits
					points
					pointsMax
					`
					: ''
			}
		}
	}
`;

const CREATE_CREQ_MUTATION = gql`
	mutation createCertRequirement($request: CreateCertRequirementRequest!) {
		createCertRequirement(request: $request) {
			id
			docUploadUrl
		}
	}
`;

const GET_CREQ_QUERY = (guard: Guard) => gql`
	query candidateAccount($cand: CandidateIdRequest, $creq: RequestByIdOrExternalId!) {
		candidateAccount(request: $cand) {
			cmcId
			externalId
			certRequirementDetail(request: $creq) {
				invalid
				cmcId
				docUrl
				docNotes
				docLanguage
				${
					guard
						.reset()
						.everyFeature([HubFeature.CredentialContinuingEducation])
						.somePermissions([
							HubPermission.CandidateRequirementsContinuingEducationView,
							HubPermission.RequirementsContinuingEducationCatalogView,
						]).all
						? `
					points
					`
						: ''
				}
				certRequirementCatalogDetail {
					id
					externalId
					instructions
					docRequired
					${
						guard
							.reset()
							.everyFeature([HubFeature.CredentialContinuingEducation])
							.somePermissions([
								HubPermission.CandidateRequirementsContinuingEducationView,
								HubPermission.RequirementsContinuingEducationCatalogView,
							]).all
							? `
					factor
					factorUnits
					points
					pointsMax
					`
							: ''
					}
				}
				activeDate
				expireDate
				externalId
			}
		}
	}
`;

@UntilDestroy()
@Component({
	selector: 'cm-upload-form',
	templateUrl: './upload-form.component.html',
	styleUrls: ['./upload-form.component.scss'],
	providers: [
		LocalizationProvider({
			...spUploadFormLocs,
			...cpUploadFormLocs,
			...cpUploadConfirmationLocs,
			...spUploadConfirmationLocs,
		}),
	],
	standalone: false,
})
export class CmUploadFormComponent implements OnInit {
	@Input() cmcId$: Observable<number>;
	@Output() next = new EventEmitter<void>();
	crcat$: Observable<CertRequirementCatalogDetail>;
	cocat$: Observable<number>;
	cultures$: Observable<CMOption[]>;
	overrideExpiration$: Observable<boolean>;
	formGroup: UntypedFormGroup;
	today = new Date();
	readonly isSP: boolean;
	crcatType = CrcatType;
	private ceAccess$: Observable<{ view: boolean; modify: boolean }>;

	private minDateSnapshot: Date;

	constructor(
		private readonly parentForm: FormGroupDirective,
		private readonly apollo: Apollo,
		private readonly uploadService: CmCreqUploadService,
		private readonly settings: SettingsService,
		private readonly fileService: AtsFileUploadService,
		private readonly auth: CmAuthService,
		private readonly notifications: AtsNotificationService,
		@Inject(LOC_INJECTOR)
		public readonly locs: CPUploadConfirmationLocKeys &
			SPUploadConfirmationLocKeys &
			CPUploadFormLocKeys &
			SPUploadFormLocKeys
	) {
		this.isSP = this.settings.portal === PortalType.SponsorPortal;
	}

	// Angular Material But
	// https://stackoverflow.com/questions/48216330/angular-5-formgroup-reset-doesnt-reset-validators
	// https://github.com/angular/angular/issues/15741

	ngOnInit() {
		this.formGroup = this.parentForm.form;
		this.parentForm.form.reset();
		this.overrideExpiration$ = this.overrideExpireDate.valueChanges.pipe(
			distinctUntilChanged(),
			startWith(false)
		);
		this.crcat$ = this.uploadService.selection$.pipe(
			filter(selection => !!selection.name),
			switchMap(selection => this.fetchCRCat$(selection.name)),
			distinctUntilKeyChanged('id')
		);
		this.cocat$ = this.uploadService.selection$.pipe(
			map(selection => selection.overrideName)
		);

		this.cultures$ = this.fetchCreqCultures$().pipe(
			map(cultures =>
				cultures.map(culture => ({ display: culture.label, value: culture.name }))
			)
		);

		this.ceAccess$ = this.auth.guard$.pipe(
			map(guard => ({
				view: guard
					.reset()
					.everyFeature([HubFeature.CredentialContinuingEducation])
					.everyPermission([HubPermission.CandidateRequirementsContinuingEducationView])
					.all,
				modify: guard
					.reset()
					.everyFeature([HubFeature.CredentialContinuingEducation])
					.everyPermission([HubPermission.CandidateRequirementsContinuingEducationModify])
					.all,
			}))
		);

		combineLatest([this.crcat$, this.ceAccess$])
			.pipe(untilDestroyed(this))
			.subscribe(([crcat, ceAccess]) => {
				if (crcat.providerRequired !== null && this.provider.disabled) {
					this.provider.enable();
				}

				if (crcat.providerRequired) {
					this.provider.setValidators([Validators.required]);
				} else {
					this.provider.setValidators(null);
					if (crcat.providerRequired === null) {
						this.provider.disable();
					}
				}

				if (!this.isSP) {
					this.expireDate.disable();
				}

				if (crcat.createAge === 0) {
					this.activeDate.setValue(new Date());
					this.activeDate.disable();
				} else if (crcat.createAge === null) {
					this.minDateSnapshot = crcat.startDate ? new Date(crcat.startDate) : null;
				} else if (crcat.createAge !== null) {
					const now = new Date();
					const date1 = crcat.startDate ? new Date(crcat.startDate) : null;
					const date2 = crcat.createAge
						? new Date(now.setMonth(now.getMonth() + -1 * crcat.createAge))
						: null;
					this.minDateSnapshot = date2 > date1 ? date2 : date1;
				}

				if (!ceAccess.modify || crcat.type !== CrcatType.CeActivity) {
					this.points.disable();
				} else {
					this.points.enable();
					this.setPoints(crcat);
				}

				if (crcat.docRequired === null) {
					this.documentation.disable();
					this.documentationLang.disable();
				} else if (!crcat.docRequired || this.isSP) {
					this.documentation.setValidators(DEFAULT_DOCUMENTATION_VALIDATORS);
					this.documentation.enable();
					this.documentationLang.enable();
					this.setDocumentationLanguage();
				} else {
					this.documentation.setValidators([
						Validators.required,
						...DEFAULT_DOCUMENTATION_VALIDATORS,
					]);
					this.documentation.enable();
					this.documentationLang.enable();
					this.setDocumentationLanguage();
				}

				if (crcat.validPeriod) {
					const now = new Date();
					this.expireDate.setValue(
						new Date(now.setMonth(now.getMonth() + crcat.validPeriod))
					);
				}

				if (this.documentation.value) {
					this.documentation.setValue(null);
				}

				this.parentForm.form.markAsPristine();
				this.parentForm.form.markAsUntouched();
			});
	}

	private setPoints(crcat: CertRequirementCatalogDetail): void {
		const catalogPoints = (crcat || {}).points;
		const pointsMax = (crcat || {}).pointsMax;
		const factor = (crcat || {}).factor ? (crcat || {}).factor : 1;

		if (pointsMax > 0 && catalogPoints > 0 && pointsMax === catalogPoints) {
			this.points.setValue(catalogPoints);
			this.points.disable();
		} else {
			this.points.setValidators([
				Validators.required,
				CmValidators.numeric(CmNumericValidatorVariation.Positive),
				CmValidators.numeric(CmNumericValidatorVariation.Integer),
			]);
			if (pointsMax > 0) {
				this.points.addValidators(Validators.max(pointsMax));
			}

			if (catalogPoints > 0) {
				this.points.addValidators(Validators.min(catalogPoints));
				this.points.patchValue(catalogPoints);
				this.points.updateValueAndValidity();
			}

			if (factor > 0 && factor !== 1) {
				this.points.addValidators(CmValidators.moduloValidator(factor, 0));
			}
		}
	}

	get activeDate() {
		return this.formGroup.get('activeDate');
	}
	get expireDate() {
		return this.formGroup.get('expireDate');
	}
	get overrideExpireDate() {
		return this.formGroup.get('overrideExpireDate');
	}
	get points() {
		return this.formGroup.get('points');
	}
	get provider() {
		return this.formGroup.get('provider');
	}
	get notes() {
		return this.formGroup.get('notes');
	}
	get comments() {
		return this.formGroup.get('comments');
	}
	get documentationLang() {
		return this.formGroup.get('documentationLang');
	}
	get documentation() {
		return this.formGroup.get('documentation');
	}
	get minDate() {
		return this.minDateSnapshot && !this.isSP ? this.minDateSnapshot : null;
	}

	handleSubmit() {
		this.formGroup.markAllAsTouched();
		this.formGroup.updateValueAndValidity();

		if (this.documentation.errors) {
			Object.keys(this.documentation.errors).forEach(err => {
				let locKey = '';
				switch (err) {
					case 'required':
						locKey = this.isSP
							? this.locs.SPUploadFormFieldErrorDocumentationRequired
							: this.locs.CPUploadFormFieldErrorDocumentationRequired;
						break;
					case 'fileSize':
						locKey = this.isSP
							? this.locs.SPUploadFormFieldErrorDocumentationSize
							: this.locs.CPUploadFormFieldErrorDocumentationSize;
						break;
					case 'maxlength':
						locKey = this.isSP
							? this.locs.SPUploadFormFieldErrorDocumentationLimit
							: this.locs.CPUploadFormFieldErrorDocumentationLimit;
						break;
				}
				if (locKey) {
					this.notifications.showFailure(locKey);
				}
			});
		}

		if (!this.formGroup.valid) {
			return;
		}
		const cmcId$ = this.cmcId$.pipe(distinctUntilChanged());
		const crcat$ = this.crcat$.pipe(distinctUntilChanged());
		const cocat$ = this.cocat$.pipe(distinctUntilChanged());

		combineLatest([cmcId$, crcat$, cocat$, this.ceAccess$])
			.pipe(
				take(1),
				map(([cmcId, crcat, cocat, ceAccess]) => {
					const request = {
						cmcId,
						crcatId: crcat.id,
						activeDate: this.activeDate.value,
						type: crcat.type,
					};

					if (cocat) {
						request['cocatId'] = cocat;
					}

					if (this.overrideExpireDate.value) {
						request['expireDate'] = this.expireDate.value;
					}

					if (this.provider.value) {
						request['provider'] = this.provider.value;
					}

					if (this.documentationLang.value) {
						request['docLanguage'] = this.documentationLang.value;
					}

					if (this.notes.value) {
						request['docNotes'] = this.notes.value;
					}

					// CE Activity and user has CE Modify permission
					if (crcat.type === CrcatType.CeActivity && ceAccess.modify) {
						const points = Number(this.points.value);
						// Make sure points is a number (otherwise it doesn't have any)
						if (!isNaN(points)) {
							request['points'] = Math.floor(points);
						}
					}

					if (this.comments.value) {
						request['historyComment'] = this.comments.value;
					}

					return request;
				}),
				withLatestFrom(this.auth.guard$),
				switchMap(([data, guard]) => {
					const { type, ...request } = data;
					const refetchQueries: any[] = [
						{
							query: CAND_CRCAT_QUERY(guard),
							variables: {
								cmcId: { cmcId: request.cmcId },
								types: { types: [type] },
							},
						},
					];
					if (
						guard
							.reset()
							.everyFeature([HubFeature.CredentialContinuingEducation])
							.everyPermission([
								HubPermission.CandidateRequirementsContinuingEducationView,
							]).all
					) {
						refetchQueries.push({
							query: CE_PROGRESS_QUERY,
							variables: {
								cmcId: { cmcId: request.cmcId },
								certRequest: { includeEligibilityCerts: true },
							},
						});
					}
					return this.apollo
						.mutate<{ createCertRequirement: Partial<CertRequirementDetail> }>({
							mutation: CREATE_CREQ_MUTATION,
							variables: { request },
							refetchQueries,
						})
						.pipe(
							map(results => ({
								creq: ((results || {}).data || {}).createCertRequirement || {},
								cmcId: request.cmcId,
							}))
						);
				}),
				switchMap(({ creq, cmcId }) => {
					return this.documentation?.value
						? this.fileService
								.uploadFile(this.documentation.value, creq.docUploadUrl)
								.pipe(map(() => ({ creq, cmcId })))
						: of({ creq, cmcId });
				}),
				withLatestFrom(this.auth.guard$),
				switchMap(([{ creq, cmcId }, guard]) => {
					return this.apollo.query<{ candidateAccount: CandidateAccount }>({
						query: GET_CREQ_QUERY(guard),
						variables: {
							cand: { cmcId },
							creq: { id: creq.id },
						},
					});
				})
			)
			.subscribe(({ data }) => {
				this.uploadService.setCReq((data.candidateAccount || {}).certRequirementDetail);
				this.next.emit();
			});
	}

	private getRefetchQueries(
		guard: Guard,
		cmcId: number,
		type: CrcatType
	): { query: DocumentNode; variables: any }[] {
		return [
			{ query: CAND_CRCAT_QUERY(guard), variables: { cmcId, types: { types: [type] } } },
			{
				query: CE_PROGRESS_QUERY,
				variables: { cmcId, certRequest: { includeEligibilityCerts: true } },
			},
		];
	}

	private setDocumentationLanguage(): void {
		if (!this.documentationLang.value) {
			this.cultures$.pipe(take(1)).subscribe(cultures => {
				if (cultures.map(culture => culture.value).includes('en')) {
					this.documentationLang.setValue('en');
				}
			});
		}
	}

	private fetchCRCat$(id: number): Observable<CertRequirementCatalogDetail> {
		return this.auth.guard$.pipe(
			switchMap(guard =>
				this.apollo
					.watchQuery<{ certRequirementCatalogDetail: CertRequirementCatalogDetail }>({
						query: CRCAT_QUERY(guard),
						variables: { request: { id } },
					})
					.valueChanges.pipe(map(({ data }) => (data || {}).certRequirementCatalogDetail))
			)
		);
	}

	private fetchCreqCultures$(): Observable<Partial<Culture>[]> {
		return this.apollo
			.watchQuery<{ cultures: Partial<Culture>[] }>({
				query: CREQ_CULTURES,
				variables: { request: { onlyNeutral: true } },
			})
			.valueChanges.pipe(map(({ data }) => (data || {}).cultures));
	}
}
